import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { Link2, Youtube, Video, Globe, Play } from "lucide-react";

const PLATFORM_ICON = { youtube: Youtube, vimeo: Video, instagram: Link2, website: Globe };

// Freelancer Portal Upgrade Phase 3 (2026-02), design pass 2026-02: renders the REAL og:image/
// oEmbed thumbnail the backend's GET /v1/community/link-preview already fetches server-side
// (YouTube/Vimeo oEmbed, generic OpenGraph scrape for a plain website) — Instagram is the ONE
// deliberate exception, always a static fallback card, never scraped (founder's explicit
// decision). A slow/broken third-party site degrades to the same clean fallback, never a
// broken image or a raw URL.
export function LinkPreviewCard({ url }) {
  const [preview, setPreview] = useState(null);
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/v1/community/link-preview", { params: { url } }).then((r) => { if (active) setPreview(r.data); }).catch(() => { if (active) setPreview({ fallback: true, platform: "website", title: url, url }); });
    return () => { active = false; };
  }, [url]);

  useEffect(() => {
    if (!preview?.thumbnail_url) { setImgSrc(null); return; }
    // Batch polish (2026-08): YouTube's oEmbed already returns a decent "hqdefault" (480×360),
    // but a card can still be wider than that — try the sharper "maxresdefault" first (not
    // always rendered for older/less-viewed videos) and fall back to the oEmbed thumbnail on
    // its very first load error, so this never risks a broken image.
    if (preview.platform === "youtube") {
      const videoId = preview.thumbnail_url.match(/\/vi\/([^/]+)\//)?.[1];
      setImgSrc(videoId ? `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg` : preview.thumbnail_url);
    } else {
      setImgSrc(preview.thumbnail_url);
    }
  }, [preview]);

  if (!preview) {
    return <div className="mt-2.5 h-32 rounded-lg border border-hairline bg-surface2/30 animate-pulse" data-testid="community-link-preview-loading" />;
  }

  const Icon = PLATFORM_ICON[preview.platform] || Globe;
  const isVideo = preview.platform === "youtube" || preview.platform === "vimeo";

  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="community-link-preview-card"
      className="mt-2.5 block max-w-md overflow-hidden rounded-lg border border-hairline hover:border-brand/50 transition-colors group">
      {!preview.fallback && imgSrc ? (
        <div className="relative aspect-video bg-ink overflow-hidden">
          <img src={imgSrc} alt={preview.title || "Link preview"} className="h-full w-full object-cover" data-testid="community-link-preview-thumbnail"
            onError={() => { if (imgSrc !== preview.thumbnail_url) setImgSrc(preview.thumbnail_url); }} loading="lazy" decoding="async" />
          {isVideo && (
            <div className="absolute inset-0 flex items-center justify-center bg-ink/10 group-hover:bg-ink/25 transition-colors">
              <div className="h-11 w-11 rounded-full bg-paper/90 flex items-center justify-center shadow-md"><Play className="h-4.5 w-4.5 text-ink fill-current" /></div>
            </div>
          )}
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-ink/80 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-paper">
            <Icon className="h-2.5 w-2.5" /> {preview.platform}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-surface2/40 px-3.5 py-3" data-testid="community-link-preview-fallback">
          <div className="h-10 w-10 rounded-full bg-ink flex items-center justify-center shrink-0"><Icon className="h-4.5 w-4.5 text-brand" /></div>
          <div className="min-w-0 text-xs text-ink-soft truncate">{preview.title || url}</div>
        </div>
      )}
      {!preview.fallback && preview.title && (
        <div className="px-3.5 py-2.5 bg-surface2/30">
          <div className="text-xs font-medium text-ink truncate">{preview.title}</div>
          {preview.description && <div className="text-[11px] text-faint truncate mt-0.5">{preview.description}</div>}
        </div>
      )}
    </a>
  );
}
